import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi, clearAdminToken } from '../../api/admin';
import type { AdminPlanet, AdminPlanetDetail, AdminUniverse } from '../../api/admin';
import {
  Shield, Search, LogOut, Eye, Coins, X, ChevronUp, ChevronDown,
  User, Globe, Sparkles, BarChart3, ArrowUpCircle, ArrowDownCircle,
  Orbit, Clock, Layers, Archive, Save, Edit3, Users,
} from 'lucide-react';
import AdminPlanetViewer from './AdminPlanetViewer';
import UpgradeManager from './UpgradeManager';

export default function AdminDashboard({ onLogout }: { onLogout: () => void }) {
  const [adminView, setAdminView] = useState<'planets' | 'developments' | 'universes'>('planets');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<'name' | 'stage' | 'balance' | 'created_at'>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedPlanet, setSelectedPlanet] = useState<AdminPlanet | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewingPlanetId, setViewingPlanetId] = useState<string | null>(null);
  const [newUniverseName, setNewUniverseName] = useState('Public Universe');
  const [newUniverseCapacity, setNewUniverseCapacity] = useState(200);
  const [universeView, setUniverseView] = useState<'active' | 'archive'>('active');
  const [universeSearch, setUniverseSearch] = useState('');
  const [selectedUniverse, setSelectedUniverse] = useState<AdminUniverse | null>(null);
  const [editingUniverseId, setEditingUniverseId] = useState<string | null>(null);
  const [editingUniverseName, setEditingUniverseName] = useState('');
  const queryClient = useQueryClient();

  const { data: planetsData, isLoading } = useQuery({
    queryKey: ['admin', 'planets'],
    queryFn: () => adminApi.getPlanets(),
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['admin', 'planet', selectedPlanet?.planet_id],
    queryFn: () => adminApi.getPlanet(selectedPlanet!.planet_id),
    enabled: !!selectedPlanet && detailOpen,
  });

  const { data: activeUniversesData } = useQuery({
    queryKey: ['admin', 'universes', 'active'],
    queryFn: () => adminApi.getUniverses('false'),
  });

  const { data: archivedUniversesData } = useQuery({
    queryKey: ['admin', 'universes', 'archive'],
    queryFn: () => adminApi.getUniverses('true'),
  });

  const { data: universeRosterData, isLoading: universeRosterLoading } = useQuery({
    queryKey: ['admin', 'universeRoster', selectedUniverse?.universe_id],
    queryFn: () => adminApi.getUniverseRoster(selectedUniverse!.universe_id),
    enabled: !!selectedUniverse,
  });

  const adjustCredits = useMutation({
    mutationFn: ({ planetId, amount }: { planetId: string; amount: number }) =>
      adminApi.adjustCredits(planetId, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'planets'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'planet', selectedPlanet?.planet_id] });
    },
  });

  const createUniverse = useMutation({
    mutationFn: () => adminApi.createUniverse(newUniverseName.trim() || 'Public Universe', newUniverseCapacity),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'universes'] });
      setNewUniverseName('Public Universe');
    },
  });

  const updateUniverse = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { paused?: boolean; archive?: boolean; resetSocial?: boolean; name?: string; capacity?: number; signupsEnabled?: boolean; leaderboardEnabled?: boolean; visibilityEnabled?: boolean } }) =>
      adminApi.updateUniverse(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'universes'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'universeRoster'] });
      setEditingUniverseId(null);
    },
  });

  const planets = planetsData?.data || [];
  const activeUniverses = activeUniversesData?.data || [];
  const archivedUniverses = archivedUniversesData?.data || [];
  const universes = universeView === 'active' ? activeUniverses : archivedUniverses;
  const filteredUniverses = universes.filter((universe) => {
    const q = universeSearch.toLowerCase();
    return (
      universe.name.toLowerCase().includes(q) ||
      universe.universe_type.toLowerCase().includes(q) ||
      (universe.join_code || '').toLowerCase().includes(q) ||
      universe.universe_id.toLowerCase().includes(q)
    );
  });

  const filtered = planets.filter((p) => {
    const q = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(q) ||
      p.user.name.toLowerCase().includes(q) ||
      p.user.email.toLowerCase().includes(q) ||
      p.planet_id.toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'name') cmp = a.name.localeCompare(b.name);
    else if (sortField === 'stage') cmp = a.stage - b.stage;
    else if (sortField === 'balance') cmp = a.balance - b.balance;
    else if (sortField === 'created_at')
      cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: typeof sortField }) => {
    if (sortField !== field) return <span className="w-3 h-3 inline-block" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  const openDetail = (planet: AdminPlanet) => {
    setSelectedPlanet(planet);
    setDetailOpen(true);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 border border-amber-500/20">
              <Shield className="h-4 w-4 text-amber-400" />
            </div>
            <div>
              <h1 className="text-sm font-semibold text-white">Admin Panel</h1>
              <p className="text-[10px] text-slate-400">Planet Builder Beta</p>
            </div>
          </div>
          <button
            onClick={() => {
              clearAdminToken();
              onLogout();
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <StatCard icon={Globe} label="Planets" value={planets.length} color="text-cyan-400" bg="bg-cyan-400/10 border-cyan-400/20" />
          <StatCard icon={User} label="Students" value={new Set(planets.filter((p) => p.user.role === 'student').map((p) => p.user.user_id)).size} color="text-emerald-400" bg="bg-emerald-400/10 border-emerald-400/20" />
          <StatCard icon={Sparkles} label="Avg Stage" value={planets.length ? (planets.reduce((s, p) => s + p.stage, 0) / planets.length).toFixed(1) : '0'} color="text-amber-400" bg="bg-amber-400/10 border-amber-400/20" />
          <StatCard icon={Coins} label="Total Credits" value={planets.reduce((s, p) => s + p.balance, 0).toLocaleString()} color="text-violet-400" bg="bg-violet-400/10 border-violet-400/20" />
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 rounded-lg border border-slate-800 bg-slate-950/60 p-1 mb-6 text-xs w-fit">
          <button
            onClick={() => setAdminView('planets')}
            className={`rounded-md px-4 py-1.5 font-medium transition-colors ${adminView === 'planets' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            Planets
          </button>
          <button
            onClick={() => setAdminView('developments')}
            className={`rounded-md px-4 py-1.5 font-medium transition-colors ${adminView === 'developments' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            Developments
          </button>
          <button
            onClick={() => setAdminView('universes')}
            className={`rounded-md px-4 py-1.5 font-medium transition-colors ${adminView === 'universes' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            Universes
          </button>
        </div>

        {adminView === 'universes' && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 mb-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between mb-4">
            <div>
              <h2 className="text-sm font-semibold text-white">Universe Management</h2>
              <p className="text-xs text-slate-500">Create public universes, inspect rosters, manage signups, and move closed universes to archive.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                value={newUniverseName}
                onChange={(e) => setNewUniverseName(e.target.value)}
                className="w-44 rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-xs text-white"
                placeholder="Universe name"
              />
              <select
                value={newUniverseCapacity}
                onChange={(e) => setNewUniverseCapacity(Number(e.target.value))}
                className="rounded-lg border border-slate-700 bg-slate-800 px-3 py-2 text-xs text-white"
              >
                {[50, 100, 150, 200].map((capacity) => <option key={capacity} value={capacity}>{capacity}</option>)}
              </select>
              <button
                onClick={() => createUniverse.mutate()}
                disabled={createUniverse.isPending}
                className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-medium text-white hover:bg-amber-500 disabled:bg-slate-700"
              >
                Create Public
              </button>
            </div>
          </div>
          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="grid grid-cols-2 gap-1 rounded-lg border border-slate-800 bg-slate-950/60 p-1 text-xs">
              <button
                onClick={() => setUniverseView('active')}
                className={`rounded-md px-3 py-1.5 ${universeView === 'active' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
              >
                Active ({activeUniverses.length})
              </button>
              <button
                onClick={() => setUniverseView('archive')}
                className={`rounded-md px-3 py-1.5 ${universeView === 'archive' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
              >
                Archive ({archivedUniverses.length})
              </button>
            </div>
            <input
              value={universeSearch}
              onChange={(e) => setUniverseSearch(e.target.value)}
              className="w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs text-white placeholder-slate-500 sm:w-64"
              placeholder="Search universes..."
            />
          </div>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {filteredUniverses.length === 0 ? (
              <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-4 text-sm text-slate-500">
                {universeView === 'archive' ? 'No archived universes.' : 'No active universes.'}
              </div>
            ) : filteredUniverses.map((universe) => (
              <div key={universe.universe_id} className="rounded-lg border border-slate-800 bg-slate-950/50 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    {editingUniverseId === universe.universe_id ? (
                      <div className="flex gap-1">
                        <input
                          value={editingUniverseName}
                          onChange={(e) => setEditingUniverseName(e.target.value)}
                          className="min-w-0 flex-1 rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-xs text-white"
                        />
                        <button
                          onClick={() => updateUniverse.mutate({ id: universe.universe_id, data: { name: editingUniverseName.trim() || universe.name } })}
                          className="rounded-md bg-emerald-700 px-2 py-1 text-white"
                          title="Save name"
                        >
                          <Save className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <div className="truncate text-sm font-medium text-white">{universe.name}</div>
                        <button
                          onClick={() => {
                            setEditingUniverseId(universe.universe_id);
                            setEditingUniverseName(universe.name);
                          }}
                          className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-white"
                          title="Rename universe"
                        >
                          <Edit3 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500">{universe._count.planets}/{universe.capacity} planets · {universe.universe_type}</div>
                    {universe.join_code && <div className="mt-0.5 text-[10px] font-mono text-cyan-300">Code {universe.join_code}</div>}
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${universe.archived ? 'bg-slate-600/25 text-slate-300' : universe.paused ? 'bg-yellow-500/15 text-yellow-300' : 'bg-emerald-500/15 text-emerald-300'}`}>
                    {universe.archived ? 'Archived' : universe.paused ? 'Paused' : 'Active'}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] text-slate-400">
                  <span>Signals {universe.socialHealth.signals}</span>
                  <span>Missions {universe.socialHealth.missions}</span>
                  <span>Tension {universe.socialHealth.averageTension}</span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1 text-[10px]">
                  <button
                    onClick={() => updateUniverse.mutate({ id: universe.universe_id, data: { signupsEnabled: !universe.signupsEnabled } })}
                    className={`rounded-md px-2 py-1 ${universe.signupsEnabled ? 'bg-emerald-950/70 text-emerald-300' : 'bg-slate-800 text-slate-400'}`}
                  >
                    Signups {universe.signupsEnabled ? 'On' : 'Off'}
                  </button>
                  <button
                    onClick={() => updateUniverse.mutate({ id: universe.universe_id, data: { leaderboardEnabled: !universe.leaderboardEnabled } })}
                    className={`rounded-md px-2 py-1 ${universe.leaderboardEnabled ? 'bg-cyan-950/70 text-cyan-300' : 'bg-slate-800 text-slate-400'}`}
                  >
                    Board {universe.leaderboardEnabled ? 'On' : 'Off'}
                  </button>
                  <button
                    onClick={() => updateUniverse.mutate({ id: universe.universe_id, data: { visibilityEnabled: !universe.visibilityEnabled } })}
                    className={`rounded-md px-2 py-1 ${universe.visibilityEnabled ? 'bg-violet-950/70 text-violet-300' : 'bg-slate-800 text-slate-400'}`}
                  >
                    Visibility {universe.visibilityEnabled ? 'On' : 'Off'}
                  </button>
                  <select
                    value={universe.capacity}
                    onChange={(e) => updateUniverse.mutate({ id: universe.universe_id, data: { capacity: Number(e.target.value) } })}
                    className="rounded-md border border-slate-700 bg-slate-800 px-2 py-1 text-slate-300"
                  >
                    {[50, 100, 150, 200].map((capacity) => <option key={capacity} value={capacity}>{capacity} slots</option>)}
                  </select>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <button
                    onClick={() => setSelectedUniverse(universe)}
                    className="rounded-md bg-slate-800 px-2 py-1 text-[10px] text-slate-300 hover:bg-slate-700"
                  >
                    Roster
                  </button>
                  <button
                    onClick={() => updateUniverse.mutate({ id: universe.universe_id, data: { paused: !universe.paused } })}
                    className="rounded-md bg-slate-800 px-2 py-1 text-[10px] text-slate-300 hover:bg-slate-700"
                  >
                    {universe.paused ? 'Resume' : 'Pause'}
                  </button>
                  <button
                    onClick={() => updateUniverse.mutate({ id: universe.universe_id, data: { archive: !universe.archived } })}
                    className="rounded-md bg-slate-800 px-2 py-1 text-[10px] text-slate-300 hover:bg-slate-700"
                  >
                    {universe.archived ? 'Unarchive' : 'Archive'}
                  </button>
                  <button
                    onClick={() => updateUniverse.mutate({ id: universe.universe_id, data: { resetSocial: true } })}
                    className="rounded-md bg-rose-950/70 px-2 py-1 text-[10px] text-rose-300 hover:bg-rose-900"
                  >
                    Reset Social
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {adminView === 'developments' && <UpgradeManager />}

        {adminView === 'planets' && (
        <>
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search planets, owners, emails..."
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/30 transition-all"
          />
        </div>

        {/* Table */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-xs text-slate-400">
                  <th className="text-left px-4 py-3 font-medium cursor-pointer hover:text-white" onClick={() => toggleSort('name')}>
                    <span className="flex items-center gap-1">Planet <SortIcon field="name" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-medium">Owner</th>
                  <th className="text-left px-4 py-3 font-medium cursor-pointer hover:text-white" onClick={() => toggleSort('stage')}>
                    <span className="flex items-center gap-1">Stage <SortIcon field="stage" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-medium cursor-pointer hover:text-white" onClick={() => toggleSort('balance')}>
                    <span className="flex items-center gap-1">Credits <SortIcon field="balance" /></span>
                  </th>
                  <th className="text-left px-4 py-3 font-medium">Upgrades</th>
                  <th className="text-left px-4 py-3 font-medium cursor-pointer hover:text-white" onClick={() => toggleSort('created_at')}>
                    <span className="flex items-center gap-1">Created <SortIcon field="created_at" /></span>
                  </th>
                  <th className="text-right px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      Loading planets...
                    </td>
                  </tr>
                ) : sorted.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                      {search ? 'No planets match your search.' : 'No planets yet.'}
                    </td>
                  </tr>
                ) : (
                  sorted.map((planet) => (
                    <tr key={planet.planet_id} className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Orbit className="w-3.5 h-3.5 text-cyan-400 shrink-0" />
                          <span className="font-medium text-white">{planet.name}</span>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-0.5 font-mono truncate max-w-[140px]">{planet.planet_id}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-white">{planet.user.name}</div>
                        <div className="text-[10px] text-slate-500">{planet.user.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-800 text-xs text-slate-300">
                          <BarChart3 className="w-3 h-3" />
                          {planet.stage}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-emerald-400">{planet.balance.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-400">
                        {planet._count.upgrade_instances}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-xs">
                        {new Date(planet.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openDetail(planet)}
                            className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
                            title="View details"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setViewingPlanetId(planet.planet_id)}
                            className="p-1.5 rounded-lg bg-cyan-950/50 hover:bg-cyan-900/50 text-cyan-400 hover:text-cyan-300 transition-colors border border-cyan-800/30"
                            title="View planet canvas"
                          >
                            <Globe className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </main>

      {/* Detail Modal */}
      {detailOpen && selectedPlanet && (
        <PlanetDetailModal
          planet={selectedPlanet}
          detail={detailData?.data}
          detailLoading={detailLoading}
          onClose={() => setDetailOpen(false)}
          onAdjustCredits={(amount) => adjustCredits.mutate({ planetId: selectedPlanet.planet_id, amount })}
          adjustPending={adjustCredits.isPending}
        />
      )}

      {selectedUniverse && (
        <UniverseRosterModal
          universe={selectedUniverse}
          roster={universeRosterData?.data?.roster || []}
          loading={universeRosterLoading}
          onClose={() => setSelectedUniverse(null)}
        />
      )}

      {viewingPlanetId && (
        <AdminPlanetViewer
          planetId={viewingPlanetId}
          onClose={() => setViewingPlanetId(null)}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, bg }: { icon: any; label: string; value: string | number; color: string; bg: string }) {
  return (
    <div className={`rounded-xl border ${bg} p-3`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${color}`} />
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{label}</span>
      </div>
      <div className="text-lg font-semibold text-white">{value}</div>
    </div>
  );
}

function PlanetDetailModal({
  planet,
  detail,
  detailLoading,
  onClose,
  onAdjustCredits,
  adjustPending,
}: {
  planet: AdminPlanet;
  detail?: AdminPlanetDetail;
  detailLoading: boolean;
  onClose: () => void;
  onAdjustCredits: (amount: number) => void;
  adjustPending: boolean;
}) {
  const [creditAmount, setCreditAmount] = useState('');
  const planetDetail = detail?.planet;
  const balance = detail?.balance ?? planet.balance;

  const handleAdd = () => {
    const n = parseInt(creditAmount, 10);
    if (!isNaN(n) && n > 0) onAdjustCredits(n);
  };

  const handleRemove = () => {
    const n = parseInt(creditAmount, 10);
    if (!isNaN(n) && n > 0) onAdjustCredits(-n);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-cyan-400" />
            <h2 className="text-sm font-semibold text-white">{planet.name}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {detailLoading ? (
            <div className="text-center py-8 text-slate-500 text-sm">Loading planet details...</div>
          ) : (
            <>
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                <InfoItem icon={User} label="Owner" value={planet.user.name} />
                <InfoItem icon={Layers} label="Universe" value={planet.universe.name} />
                <InfoItem icon={BarChart3} label="Stage" value={`${planet.stage}`} />
                <InfoItem icon={Clock} label="Created" value={new Date(planet.created_at).toLocaleDateString()} />
                <InfoItem icon={Coins} label="Credits" value={`${balance.toLocaleString()}`} valueColor="text-emerald-400" />
                <InfoItem icon={Sparkles} label="Upgrades" value={`${planet._count.upgrade_instances}`} />
              </div>

              {/* State preview */}
              {planetDetail && (
                <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-3">
                  <h3 className="text-xs font-medium text-slate-400 mb-2">Planet State</h3>
                  <StatePreview state={(planetDetail as any).state || {}} />
                </div>
              )}

              {/* Credit adjustment */}
              <div className="bg-slate-950/50 border border-slate-800 rounded-xl p-3">
                <h3 className="text-xs font-medium text-slate-400 mb-2">Adjust Credits</h3>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    placeholder="Amount"
                    min={1}
                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30"
                  />
                  <button
                    onClick={handleAdd}
                    disabled={adjustPending || !creditAmount}
                    className="flex items-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-xs font-medium text-white transition-colors"
                  >
                    <ArrowUpCircle className="w-3.5 h-3.5" />
                    Add
                  </button>
                  <button
                    onClick={handleRemove}
                    disabled={adjustPending || !creditAmount}
                    className="flex items-center gap-1 px-3 py-2 bg-rose-600 hover:bg-rose-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-xs font-medium text-white transition-colors"
                  >
                    <ArrowDownCircle className="w-3.5 h-3.5" />
                    Remove
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoItem({ icon: Icon, label, value, valueColor = 'text-white' }: { icon: any; label: string; value: string; valueColor?: string }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
      <div>
        <div className="text-[10px] text-slate-500">{label}</div>
        <div className={`text-xs font-medium ${valueColor}`}>{value}</div>
      </div>
    </div>
  );
}

function StatePreview({ state }: { state: Record<string, unknown> }) {
  const stats = (state.stats as Record<string, number>) || {};
  const entries = Object.entries(stats).slice(0, 10);
  if (entries.length === 0) return <div className="text-xs text-slate-500">No stats available</div>;

  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center justify-between text-xs">
          <span className="text-slate-500 capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
          <span className="font-mono text-slate-300">{typeof value === 'number' ? value.toFixed(1) : value}</span>
        </div>
      ))}
    </div>
  );
}

function UniverseRosterModal({
  universe,
  roster,
  loading,
  onClose,
}: {
  universe: AdminUniverse;
  roster: Array<{
    user: { user_id: string; name: string; email: string; active: boolean; role: string };
    planet: { planet_id: string; name: string; stage: number; created_at: string; _count?: { upgrade_instances?: number } };
    balance: { current_balance: number; lifetime_earned: number; lifetime_spent: number };
  }>;
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Archive className="w-4 h-4 text-amber-400" />
            <div>
              <h2 className="text-sm font-semibold text-white">{universe.name}</h2>
              <p className="text-[10px] text-slate-500">{universe._count.planets}/{universe.capacity} planets · {universe.universe_type}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-4">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500">Loading roster...</div>
          ) : roster.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">No planets in this universe.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-xs text-slate-400">
                  <th className="px-3 py-2 text-left font-medium">Student</th>
                  <th className="px-3 py-2 text-left font-medium">Planet</th>
                  <th className="px-3 py-2 text-left font-medium">Stage</th>
                  <th className="px-3 py-2 text-left font-medium">Credits</th>
                  <th className="px-3 py-2 text-left font-medium">Upgrades</th>
                </tr>
              </thead>
              <tbody>
                {roster.map((entry) => (
                  <tr key={entry.planet.planet_id} className="border-b border-slate-800/60">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 text-white">
                        <Users className="h-3.5 w-3.5 text-slate-500" />
                        {entry.user.name}
                      </div>
                      <div className="text-[10px] text-slate-500">{entry.user.email}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-300">{entry.planet.name}</td>
                    <td className="px-3 py-2 text-slate-300">{entry.planet.stage}</td>
                    <td className="px-3 py-2 font-mono text-emerald-400">{entry.balance.current_balance.toLocaleString()}</td>
                    <td className="px-3 py-2 text-slate-400">{entry.planet._count?.upgrade_instances || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
